import { reactive, computed } from "vue";
import { useStore } from "vuex";
import {
  createConditionalValidator,
  createNumericValidator,
  createEmailValidator,
} from "./types";

/**
 * Shared Field Service types and logic for both Rental and Force task details.
 */

export type FieldServiceInfo = {
  additionInfo: boolean;
  clientName: string;
  location: string;
  manufacturer: string;
  model: string;
  vehRegNo: string;
  serialNo: string;
  SMUHour?: number;
  travelKMS?: number;
  customerEmail: string;
  fieldServiceNote: string;
  customerAcceptance: string;
  mechanicalAcceptance: string;
  isCustomerAcceptance: string;
  isMechanicalAcceptance: string;
  customerAcceptanceName: string;
};

export const DEFAULT_FIELD_SERVICE_INFO: FieldServiceInfo = {
  additionInfo: false,
  clientName: "",
  location: "",
  manufacturer: "",
  model: "",
  vehRegNo: "",
  serialNo: "",
  SMUHour: undefined,
  travelKMS: undefined,
  customerEmail: "",
  fieldServiceNote: "",
  customerAcceptance: "",
  mechanicalAcceptance: "",
  isCustomerAcceptance: "",
  isMechanicalAcceptance: "",
  customerAcceptanceName: "",
};

export function useFieldService<T extends FieldServiceInfo>(
  state: { taskInfo: T },
  isNonProductive?: () => boolean
) {
  const store = useStore();

  const allowInputFieldService = computed(() => {
    const user = store.getters["auth/currentUser"];
    const hasFieldServicePermission = user?.showFieldService ?? true;
    
    // Hide field service for non-productive lines
    if (isNonProductive && isNonProductive()) {
      return false;
    }
    
    return hasFieldServicePermission;
  });

  const sigOption = computed(() => ({
    penColor: "rgb(0, 0, 0)",
    backgroundColor: "rgb(255,255,255)",
  }));

  // Field Service validation rules using factory functions for DRY code
  const isFieldServiceEnabled = () => state.taskInfo.additionInfo;
  const isManualFieldRequired = () => state.taskInfo.additionInfo;
  
  const fieldServiceRules = {
    clientName: [
      createConditionalValidator(isManualFieldRequired, "Please enter client name")
    ],
    location: [
      createConditionalValidator(isManualFieldRequired, "Please enter location")
    ],
    manufacturer: [
      createConditionalValidator(isManualFieldRequired, "Please enter manufacturer")
    ],
    model: [
      createConditionalValidator(isManualFieldRequired, "Please enter model")
    ],
    vehRegNo: [
      createConditionalValidator(isFieldServiceEnabled, "Please enter Veh. Reg. No")
    ],
    serialNo: [
      createConditionalValidator(isManualFieldRequired, "Please enter Serial no")
    ],
    SMUHour: [
      createNumericValidator(
        isFieldServiceEnabled,
        "Please enter SMU Hours",
        "Please enter correct SMU hours"
      )
    ],
    travelKMS: [
      createNumericValidator(
        isFieldServiceEnabled,
        "Please enter Travel KMs",
        "Please enter correct travel KMs"
      )
    ],
    fieldServiceNote: [
      createConditionalValidator(isFieldServiceEnabled, "Please enter field service note")
    ],
    customerEmail: [
      createEmailValidator(
        isFieldServiceEnabled,
        "Please enter customer email",
        "Please input correct email"
      )
    ],
    isCustomerAcceptance: [
      createConditionalValidator(isFieldServiceEnabled, "Please sign")
    ],
    isMechanicalAcceptance: [
      createConditionalValidator(isFieldServiceEnabled, "Please sign")
    ],
    customerAcceptanceName: [
      createConditionalValidator(isFieldServiceEnabled, "Please enter customer acceptance name")
    ]
  };

  // Setters
  const setFieldService = (enabled: boolean) => { state.taskInfo.additionInfo = enabled; };
  const setClientName = (name: string) => { state.taskInfo.clientName = name; };
  const setLocation = (loc: string) => { state.taskInfo.location = loc; };
  const setManufacturer = (man: string) => { state.taskInfo.manufacturer = man; };
  const setModel = (mod: string) => { state.taskInfo.model = mod; };
  const setVehRegNo = (reg: string) => { state.taskInfo.vehRegNo = reg; };
  const setSerialNo = (serial: string) => { state.taskInfo.serialNo = serial; };
  const setSMUHour = (hours: number) => { state.taskInfo.SMUHour = hours; };
  const setTravelKMS = (kms: number) => { state.taskInfo.travelKMS = kms; };
  const setCustomerEmail = (email: string) => { state.taskInfo.customerEmail = email; };
  const setFieldServiceNote = (note: string) => { state.taskInfo.fieldServiceNote = note; };
  const setCustomerAcceptanceName = (name: string) => { state.taskInfo.customerAcceptanceName = name; };

  // Signature handlers
  const handleSignatureClick = (event: any, type: "customer" | "mechanical") => {
    if (type === "customer") {
      state.taskInfo.isCustomerAcceptance = "change";
    } else {
      state.taskInfo.isMechanicalAcceptance = "change";
    }
  };

  const handleClearSignature = (type: "customer" | "mechanical", signatureRef?: any) => {
    if (type === "customer") {
      state.taskInfo.customerAcceptance = "";
      state.taskInfo.isCustomerAcceptance = "";
      if (signatureRef && signatureRef.clear) signatureRef.clear();
    } else {
      state.taskInfo.mechanicalAcceptance = "";
      state.taskInfo.isMechanicalAcceptance = "";
      if (signatureRef && signatureRef.clear) signatureRef.clear();
    }
  };

  /**
   * Convert base64 image to Blob
   */
  const convertBase64ToBlob = async (imageBase64: string): Promise<Blob> => {
    const response = await fetch(imageBase64);
    return response.blob();
  };

  /**
   * Upload a single signature and return the image path.
   * @param signatureRef - Vue3Signature ref with .save() method
   * @returns Promise with uploaded image path
   * @throws Error if upload fails
   */
  const uploadSingleSignature = async (signatureRef: any): Promise<string> => {
    if (!signatureRef || !signatureRef.save) return '';
    
    const imageBase64 = signatureRef.save('image/png');
    // If no signature drawn, return empty (not an error)
    if (!imageBase64 || imageBase64.length < 100) return '';

    const blob = await convertBase64ToBlob(imageBase64);
    const file = new File([blob], "signature.png", { type: "image/png" });
    const formData = new FormData();
    formData.append('img', file);

    const result = await store.dispatch("v2/uploadSignature", formData);
    if (!result?.image) {
      throw new Error('Failed to upload signature: No image path returned');
    }
    return result.image;
  };

  /**
   * Upload both signatures if they have been modified and update state.
   * Call this before submitting the form.
   * @param customerRef - Vue3Signature ref for customer signature
   * @param mechanicalRef - Vue3Signature ref for mechanical signature
   * @returns Promise with the uploaded paths
   */
  const uploadSignatures = async (customerRef?: any, mechanicalRef?: any): Promise<{
    customerAcceptance: string;
    mechanicalAcceptance: string;
  }> => {
    const result = {
      customerAcceptance: state.taskInfo.customerAcceptance || '',
      mechanicalAcceptance: state.taskInfo.mechanicalAcceptance || '',
    };

    // Upload customer signature if modified
    if (state.taskInfo.isCustomerAcceptance === 'change' && customerRef) {
      const path = await uploadSingleSignature(customerRef);
      if (path) {
        result.customerAcceptance = path;
        state.taskInfo.customerAcceptance = path;
      }
    }

    // Upload mechanical signature if modified
    if (state.taskInfo.isMechanicalAcceptance === 'change' && mechanicalRef) {
      const path = await uploadSingleSignature(mechanicalRef);
      if (path) {
        result.mechanicalAcceptance = path;
        state.taskInfo.mechanicalAcceptance = path;
      }
    }

    return result;
  };

  return {
    allowInputFieldService,
    sigOption,
    fieldServiceRules,
    setFieldService,
    setClientName,
    setLocation,
    setManufacturer,
    setModel,
    setVehRegNo,
    setSerialNo,
    setSMUHour,
    setTravelKMS,
    setCustomerEmail,
    setFieldServiceNote,
    setCustomerAcceptanceName,
    handleSignatureClick,
    handleClearSignature,
    uploadSignatures,
    uploadSingleSignature,
  };
}
